/**
 * A bundled link that leans on a record the same card creates (Decided L2 of
 * docs/plans/links-fixes.md), filled in once that record is made.
 *
 * The server plans the link against the record as it will be: the record's
 * id, its fields and its choices stand as placeholders, and the proposal's
 * `pendingRecord` says how each one is found on the record once it exists —
 * a field by its label, a choice by its field and its word or key. The tap
 * makes the record first, reads its schema, and hands both here; what comes
 * back is the link with real ids, which the Links route then checks in full.
 *
 * Every placeholder is a whole string, so the fill replaces whole strings
 * only, keys and values alike, wherever the request holds them (a condition,
 * a fill, a worked-out mapping's tree). A placeholder the record does not
 * hold is never guessed: the fill answers which labels are missing, and the
 * card says so.
 */
import type { LinkProposal } from './agent';
import type { RecordSchemaResponse } from './api';

export type PendingLinkFill =
  | { readonly ok: true; readonly proposal: LinkProposal }
  | { readonly ok: false; readonly missing: readonly string[] };

const folded = (text: string | null | undefined): string => (text ?? '').trim().toLowerCase();

/** Every string in `value` swapped through `ids`, object keys included. */
const swapped = (value: unknown, ids: ReadonlyMap<string, string>): unknown => {
  if (typeof value === 'string') return ids.get(value) ?? value;
  if (Array.isArray(value)) return value.map((one) => swapped(one, ids));
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(Object.entries(value).map(([key, one]) => [ids.get(key) ?? key, swapped(one, ids)]));
  }
  return value;
};

/** Whether a link still waits on a record this card has not made. */
export const isPendingLink = (proposal: LinkProposal): boolean => {
  const pending = proposal.pendingRecord;
  if (pending === undefined) return false;
  return (pending.side === 'from' ? proposal.fromRecordId : proposal.toRecordId) === pending.placeholderId;
};

/**
 * The link with the made record's id in place of its placeholder: what the
 * card shows and loads by while the record exists but the link is not posted.
 */
export const withPendingRecordId = (proposal: LinkProposal, recordId: string): LinkProposal => {
  const pending = proposal.pendingRecord;
  if (pending === undefined) return proposal;
  const ids = new Map([[pending.placeholderId, recordId]]);
  return {
    ...proposal,
    fromRecordId: ids.get(proposal.fromRecordId) ?? proposal.fromRecordId,
    toRecordId: ids.get(proposal.toRecordId) ?? proposal.toRecordId,
    request: { ...proposal.request, toRecordId: ids.get(proposal.request.toRecordId) ?? proposal.request.toRecordId },
  };
};

/**
 * The link with every placeholder swapped for the made record's real ids, or
 * the labels the record turned out not to hold. `schema` is the made record's.
 */
export const fillPendingLink = (proposal: LinkProposal, schema: RecordSchemaResponse): PendingLinkFill => {
  const pending = proposal.pendingRecord;
  if (pending === undefined) return { ok: true, proposal };
  const ids = new Map<string, string>([[pending.placeholderId, schema.recordId]]);
  const missing: string[] = [];
  for (const field of pending.fields) {
    const found = schema.fields.find((one) => folded(one.label) === folded(field.label));
    if (found === undefined) missing.push(field.label);
    else ids.set(field.id, found.id);
  }
  for (const option of pending.options) {
    const field = schema.fields.find((one) => folded(one.label) === folded(option.fieldLabel));
    const found = field?.options?.find(
      (one) =>
        (option.labelKey !== null && option.labelKey !== '' && one.labelKey === option.labelKey) ||
        (option.label !== null && folded(one.label) === folded(option.label)),
    );
    if (found === undefined) missing.push(`${option.fieldLabel}: ${option.label ?? option.labelKey ?? option.id}`);
    else ids.set(option.id, found.id);
  }
  if (missing.length > 0) return { ok: false, missing };
  const { pendingRecord: _done, ...rest } = proposal;
  return {
    ok: true,
    proposal: {
      ...rest,
      fromRecordId: ids.get(proposal.fromRecordId) ?? proposal.fromRecordId,
      toRecordId: ids.get(proposal.toRecordId) ?? proposal.toRecordId,
      request: swapped(proposal.request, ids) as LinkProposal['request'],
    },
  };
};
